import { useEffect, useState } from 'react';
import { JourneyTriggerPanel } from './journey-trigger-panel.js';
import { JourneyEmailPanel } from './journey-email-panel.js';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  api,
  type ApiError,
  type FlowNodeCountsResult,
  type GetFlowResult,
  type ListPalettesResult,
} from '@whinerp/client';
import {
  Button,
  ErrorState,
  FlowCanvas,
  Icon,
  PageHeading,
  Panel,
  SectionLabel,
  Skeleton,
  useToast,
} from '@whinerp/ui';
import type { FlowGraph, Palette } from '@whinerp/contracts/flow';
import { formatCount } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';
import { flowCanvasLabels } from '../flows/flow-labels.js';
import { defineScreen } from '../types.js';

/**
 * SC-20 Journey builder.
 *
 * The same FlowCanvas the automation builder, the chatbot builder and the run
 * log use, given the journey palette. There is no journey canvas.
 *
 * What a journey adds is the counts. A canvas without them is documentation:
 * it shows what was designed and says nothing about what is happening, and the
 * question anybody actually has in front of a live journey is how many people
 * are stuck at step four. They load separately from the graph, so the canvas
 * draws immediately and the counts arrive over it - a graph that waited for
 * them would make the whole screen feel broken while somebody edits it.
 */

function JourneyBuilderScreen(): JSX.Element {
  const t = useT();
  const format = useFormat();
  const toast = useToast();
  const params = useParams();
  const flowId = params['id'] ?? '';

  const [graph, setGraph] = useState<FlowGraph | null>(null);
  const [selected, setSelected] = useState<string | null>(null);

  const flow = useQuery<GetFlowResult, ApiError>({
    queryKey: ['flow', flowId],
    queryFn: () => api.getFlow({ path: { flowId } }),
  });

  const palettes = useQuery<ListPalettesResult, ApiError>({
    queryKey: ['palettes'],
    queryFn: () => api.listPalettes({}),
  });

  // Independent of the graph, deliberately: 04_Screens asks for the canvas to
  // render while counts stream in.
  const counts = useQuery<FlowNodeCountsResult, ApiError>({
    queryKey: ['flow-counts', flowId],
    queryFn: () => api.flowNodeCounts({ path: { flowId } }),
  });

  useEffect(() => {
    if (flow.data && graph === null) setGraph(flow.data.graph as FlowGraph);
  }, [flow.data, graph]);

  const save = useMutation({
    mutationFn: (status: 'draft' | 'active') =>
      api.saveFlow({
        path: { flowId },
        body: { graph: (graph ?? { nodes: [], edges: [] }) as never, status },
      }),
    onSuccess: (result) => {
      toast.show({
        tone: 'success',
        title: t('state.saved'),
        body: result.status === 'active' ? t('flow.active') : t('flow.draft'),
      });
      void flow.refetch();
    },
    onError: (error: ApiError) =>
      toast.show({ tone: 'error', title: t('flow.problems'), body: error.message }),
  });

  if (flow.isError) return <JourneyError error={flow.error} retry={() => void flow.refetch()} />;
  if (flow.isPending || !graph) return <JourneyLoading />;

  const palette = (palettes.data?.palettes.find((entry) => entry.key === flow.data.palette) ??
    null) as Palette | null;
  if (!palette) return <JourneyLoading />;

  const problems = flow.data.problems;
  const inJourney = (counts.data?.counts ?? []).reduce((total, node) => total + node.count, 0);

  return (
    <div data-component="JourneyBuilder" className="flex w-full flex-col gap-4">
      <header className="flex flex-wrap items-center gap-3">
        <PageHeading>{flow.data.name}</PageHeading>
        <span
          data-flow-status={flow.data.status}
          className="inline-flex h-chip items-center rounded-chip bg-sunken px-2 text-meta text-ink-600"
        >
          {flow.data.status === 'active' ? t('flow.active') : t('flow.draft')}
        </span>
        {counts.isPending ? (
          <Skeleton className="h-chip w-panel-narrow" />
        ) : (
          <span data-in-journey className="text-meta text-ink-meta">
            {t('segment.members', { values: { count: formatCount(inJourney, format) } })}
          </span>
        )}

        <span className="ms-auto flex items-center gap-2">
          <Button intent="secondary" data-save-draft onClick={() => save.mutate('draft')}>
            {t('action.save')}
          </Button>
          <Button
            intent="primary"
            data-publish
            // Blocked with the list beside it. A journey switched on while
            // incomplete strands people part-way through it, and they are
            // real people waiting for an email that will not come.
            disabled={problems.length > 0 || save.isPending}
            title={problems.length > 0 ? t('flow.problems') : undefined}
            onClick={() => save.mutate('active')}
          >
            {t('flow.activate')}
          </Button>
        </span>
      </header>

      {/* People part-way through an earlier version finish that one (G-174):
          said here, before somebody edits and wonders why they did not move. */}
      {(flow.data?.earlierVersions ?? []).length > 0 ? (
        <p className="text-dense text-ink-600" data-journey-earlier-versions>
          {t('journey.earlierVersions', {
            values: {
              count: String((flow.data?.earlierVersions ?? []).reduce((total, entry) => total + entry.inProgress, 0)),
              version: String(flow.data?.version ?? ''),
            },
          })}
        </p>
      ) : null}
      <JourneyTriggerPanel graph={graph} onChange={setGraph} />
      <JourneyEmailPanel graph={graph} onChange={setGraph} />

      {problems.length > 0 ? (
        <Panel data-region="problems">
          <div className="flex flex-col gap-1">
            <SectionLabel>{t('flow.problems')}</SectionLabel>
            <ul className="flex flex-col">
              {problems.map((problem) => (
                <li
                  key={`${problem.nodeId ?? 'flow'}-${problem.message}`}
                  className="flex items-center gap-1 text-dense text-danger-text"
                >
                  <Icon name="Warning" size="inline" decorative />
                  {problem.message}
                </li>
              ))}
            </ul>
          </div>
        </Panel>
      ) : null}

      <Panel data-region="canvas">
        <FlowCanvas
          data-journey-canvas
          graph={graph}
          palette={palette}
          labels={flowCanvasLabels(t)}
          problems={problems}
          /* The counts, on the nodes. The question in front of a live journey
             is how many people are stuck at step four. */
          counts={(counts.data?.counts ?? []).map((node) => ({
            nodeId: node.nodeId,
            // Formatted here: the canvas renders the string and never sees a
            // number, so the one formatter stays the only one (D-018).
            count: formatCount(node.count, format),
          }))}
          onChange={setGraph}
          selectedNodeId={selected}
          onSelectNode={setSelected}
        />
      </Panel>
    </div>
  );
}

function JourneyEmpty(): JSX.Element {
  const t = useT();
  return <p className="text-body text-ink-meta">{t('flow.empty.body')}</p>;
}

function JourneyLoading(): JSX.Element {
  const t = useT();
  return (
    <div className="flex w-full flex-col gap-4" role="status" aria-live="polite" aria-busy>
      <span className="sr-only">{t('state.loading')}</span>
      <Skeleton className="h-line-h1 w-block-xl" />
      {/* The canvas renders first; counts arrive over it. */}
      <Skeleton className="h-plot w-full" />
    </div>
  );
}

function JourneyBuilderError({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  return (
    <ErrorState
      title="This journey could not be loaded"
      body={error instanceof Error ? error.message : 'The journey did not load.'}
      onRetry={retry}
    />
  );
}

const JourneyError = JourneyBuilderError;

export const screen = defineScreen({
  id: 'SC-20',
  route: '/journeys/:id',
  surface: 'page',
  title: 'Journey builder',
  /*
   * F-068 and F-069.
   *
   * The claim on F-069 was withdrawn for a release because the canvas could
   * draw a behavioural trigger node and nothing fired one. `recordBehaviour`
   * now watches page views, form submissions, clicks and field changes and
   * starts the journeys that listen for them, and a wait in the middle of one
   * actually waits - so the node the palette draws is a node that runs.
   */
  features: ['F-068', 'F-069'],
  entitlement: 'F-068',
  reachedBy: [{ kind: 'nav', item: 'campaigns' }],
  components: ['FlowCanvas'],
  elements: ['JourneyBuilder'],
  sampleRecord: { objectKey: 'flow', param: 'id' },
  states: { empty: JourneyEmpty, loading: JourneyLoading, error: JourneyBuilderError },
  component: JourneyBuilderScreen,
});

export default screen;
